// services/api.js - API service layer
class ApiService {
	constructor() {
		// Check if we're in production environment
		const isProduction = process.env.NODE_ENV === 'production';

		// Use the appropriate API URL based on environment
		this.baseUrl = isProduction
			? process.env.REACT_APP_PRODUCTION_API_URL
			: process.env.REACT_APP_API_URL || '/api';

		// Extract the base server URL (protocol + host)
		this.serverUrl = '';
		if (this.baseUrl.startsWith('http')) {
			const url = new URL(this.baseUrl);
			this.serverUrl = `${url.protocol}//${url.host}`;
		} else {
			this.serverUrl = window.location.origin;
		}
	}

	// Helper method for handling fetch responses
	async handleResponse(response) {
		const contentType = response.headers.get('content-type');

		if (!response.ok) {
			// Try to parse error message from JSON response if possible
			if (contentType && contentType.includes('application/json')) {
				const errorData = await response.json();
				throw new Error(errorData.message || errorData.error || 'API request failed');
			}
			throw new Error(`HTTP error ${response.status}`);
		}

		// Return JSON response if content type is JSON
		if (contentType && contentType.includes('application/json')) {
			return response.json();
		}

		// Return blob for binary data (images)
		if (contentType && contentType.includes('image/')) {
			return response.blob();
		}

		// Default to text
		return response.text();
	}

	// Get the full URL for a relative path
	getFullUrl(path) {
		if (!path) return null;
		if (path.startsWith('http') || path.startsWith('data:')) return path;

		// If path starts with /, use serverUrl directly, otherwise append to baseUrl
		if (path.startsWith('/')) {
			return `${this.serverUrl}${path}`;
		} else {
			return `${this.serverUrl}${this.baseUrl.endsWith('/') ? this.baseUrl : `${this.baseUrl}/`}${path}`;
		}
	}

	// Helper method to get auth headers
	getAuthHeaders() {
		const token = localStorage.getItem('auth_token');
		return token ? { 'Authorization': `Bearer ${token}` } : {};
	}

	// Authentication APIs

	// Register a new user
	async register(email, password, fullName) {
		const response = await fetch(`${this.baseUrl}/auth/register`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email, password, fullName })
		});

		return this.handleResponse(response);
	}

	// Login user
	async login(email, password) {
		const response = await fetch(`${this.baseUrl}/auth/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email, password })
		});

		return this.handleResponse(response);
	}

	// Logout user
	async logout() {
		const response = await fetch(`${this.baseUrl}/auth/logout`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			}
		});

		return this.handleResponse(response);
	}

	// Get current user info
	async getCurrentUser() {
		const response = await fetch(`${this.baseUrl}/auth/me`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Request password reset
	async requestPasswordReset(email) {
		const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email })
		});

		return this.handleResponse(response);
	}

	// Reset password with token
	async resetPassword(token, newPassword) {
		const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ token, password: newPassword })
		});

		return this.handleResponse(response);
	}

	// Verify email with token
	async verifyEmail(token) {
		const response = await fetch(`${this.baseUrl}/auth/verify-email?token=${token}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			},
			cache: 'no-cache'
		});

		return this.handleResponse(response);
	}

	// Social login
	async socialLogin(provider, providerData) {
		const response = await fetch(`${this.baseUrl}/auth/social-login/${provider}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				credential: providerData.credential
			})
		});

		return this.handleResponse(response);
	}

	// Update user profile
	async updateUserProfile(profileData) {
		const response = await fetch(`${this.baseUrl}/auth/profile`, {
			method: 'PUT',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(profileData)
		});

		return this.handleResponse(response);
	}

	// Update password
	async updatePassword(currentPassword, newPassword) {
		const response = await fetch(`${this.baseUrl}/auth/password`, {
			method: 'PUT',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ currentPassword, newPassword })
		});

		return this.handleResponse(response);
	}

	// Resend verification email
	async resendVerificationEmail(email) {
		const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email })
		});

		return this.handleResponse(response);
	}

	// Subscription and Payment APIs

	// Get subscription plans
	async getSubscriptionPlans() {
		const response = await fetch(`${this.baseUrl}/subscriptions/plans`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Get user subscriptions
	async getUserSubscriptions() {
		const response = await fetch(`${this.baseUrl}/subscriptions/user`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Subscribe to a plan
	async subscribeToPlan(planId, paymentMethodId, autoRenew = true) {
		const response = await fetch(`${this.baseUrl}/payments/subscriptions`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ planId, paymentMethodId, autoRenew })
		});

		return this.handleResponse(response);
	}

	// Cancel subscription
	async cancelSubscription(subscriptionId) {
		const response = await fetch(`${this.baseUrl}/payments/subscriptions/${subscriptionId}/cancel`, {
			method: 'POST',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Get credit balance
	async getCreditBalance() {
		const response = await fetch(`${this.baseUrl}/credits/balance`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Get credit transactions
	async getCreditTransactions() {
		const response = await fetch(`${this.baseUrl}/credits/transactions`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Purchase credits
	async purchaseCredits(amount, credits) {
		const response = await fetch(`${this.baseUrl}/payments/credits/purchase`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ amount, credits })
		});

		return this.handleResponse(response);
	}

	// Create a Stripe customer
	async createStripeCustomer() {
		const response = await fetch(`${this.baseUrl}/payments/customer`, {
			method: 'POST',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Add a payment method
	async addPaymentMethod(paymentMethodId) {
		const response = await fetch(`${this.baseUrl}/payments/methods`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ paymentMethodId })
		});

		return this.handleResponse(response);
	}

	// Get payment methods
	async getPaymentMethods() {
		const response = await fetch(`${this.baseUrl}/payments/methods`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Verify subscription checkout session
	async verifySubscriptionCheckout(sessionId) {
		const response = await fetch(`${this.baseUrl}/payments/subscriptions/verify-session`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ sessionId })
		});

		return this.handleResponse(response);
	}

	// Verify credit purchase checkout session
	async verifyCreditPurchaseCheckout(sessionId) {
		const response = await fetch(`${this.baseUrl}/payments/credits/verify-session`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ sessionId })
		});

		return this.handleResponse(response);
	}

	// Furniture Inpainting API
	async inpaintFurniture(imageFile, maskFile, params) {
		const formData = new FormData();

		// Add the image file
		formData.append('image', imageFile);

		// Add the mask file if it exists
		if (maskFile) {
			formData.append('mask', maskFile);
		}

		// Add other parameters
		if (params.mask_area) {
			formData.append('mask_area', params.mask_area);
		}

		formData.append('prompt', params.prompt);

		if (params.negativePrompt) {
			formData.append('negative_prompt', params.negativePrompt);
		}

		if (params.stylePreset) {
			formData.append('style_preset', params.stylePreset);
		}

		const response = await fetch(`${this.baseUrl}/inpaint/inpaint`, {
			method: 'POST',
			headers: this.getAuthHeaders(),
			body: formData
		});

		return this.handleResponse(response);
	}

	// Save mask API
	async saveMask(maskDataUrl) {
		const response = await fetch(`${this.baseUrl}/inpaint/save-mask`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ maskData: maskDataUrl })
		});

		return this.handleResponse(response);
	}

	// Search and Replace API
	async searchAndReplace(imageFile, params) {
		const formData = new FormData();

		// Add the image file
		formData.append('image', imageFile);

		// Add required parameters - match the backend parameter names
		formData.append('search_prompt', params.searchObject || params.searchPrompt);
		formData.append('prompt', params.replaceObject || params.replacePrompt);

		// Add optional parameters
		if (params.negativePrompt) {
			formData.append('negative_prompt', params.negativePrompt);
		}

		if (params.stylePreset) {
			formData.append('style_preset', params.stylePreset);
		}

		if (params.cfgScale) {
			formData.append('cfg_scale', params.cfgScale);
		}

		if (params.steps) {
			formData.append('steps', params.steps);
		}

		const endpoint = this.isAuthenticated() ? 'search-replace' : 'search-replace/public';
		const response = await fetch(`${this.baseUrl}/inpaint/${endpoint}`, {
			method: 'POST',
			headers: {
				// Don't set Content-Type when sending FormData
				// The browser will set it automatically with the correct boundary
				...this.getAuthHeaders()
			},
			body: formData
		});

		return this.handleResponse(response);
	}

	// Search and Recolor API
	async searchAndRecolor(imageFile, params) {
		const formData = new FormData();

		// Add the image file
		formData.append('image', imageFile);

		// Add required parameters - match the backend parameter names
		formData.append('select_prompt', params.searchObject || params.searchPrompt);
		formData.append('prompt', params.replaceObject || params.replacePrompt || params.colorPrompt);

		// Add optional parameters
		if (params.negativePrompt) {
			formData.append('negative_prompt', params.negativePrompt);
		}

		if (params.stylePreset) {
			formData.append('style_preset', params.stylePreset);
		}

		if (params.imageStrength) {
			formData.append('image_strength', params.imageStrength);
		}

		if (params.cfgScale) {
			formData.append('cfg_scale', params.cfgScale);
		}

		if (params.steps) {
			formData.append('steps', params.steps);
		}

		const endpoint = this.isAuthenticated() ? 'search-recolor' : 'search-recolor/public';
		const response = await fetch(`${this.baseUrl}/inpaint/${endpoint}`, {
			method: 'POST',
			headers: {
				...this.getAuthHeaders()
			},
			body: formData
		});

		return this.handleResponse(response);
	}

	// Get user's image history
	async getUserImageHistory(limit = 20, offset = 0, type = null, sortField = 'created_at', sortDirection = 'desc') {
		let url = `${this.baseUrl}/files?limit=${limit}&offset=${offset}`;

		if (type) {
			url += `&type=${type}`;
		}

		// Add sorting parameters to the URL
		url += `&sortField=${sortField}&sortDirection=${sortDirection}`;
		const response = await fetch(url, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		const data = await this.handleResponse(response);

		// The backend now returns an object with files and total properties
		// If for some reason we still get the old format (direct array), handle it as before
		if (Array.isArray(data)) {
			const files = data;
			const total = files.reduce((sum, img) =>
				sum + (img.generatedImage ? img.generatedImage.size : img.size || 0), 0);

			return { files, total };
		}

		// Use the server-provided total if available
		return {
			files: data.files || [],
			total: data.total || 0
		};
	}

	// Delete a user file
	async deleteUserFile(fileId) {
		const response = await fetch(`${this.baseUrl}/files/${fileId}`, {
			method: 'DELETE',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Get a specific user image
	async getUserImage(imageId) {
		const response = await fetch(`${this.baseUrl}/files/${imageId}`, {
			method: 'GET',
			headers: this.getAuthHeaders()
		});

		return this.handleResponse(response);
	}

	// Save an edited image
	async saveEditedImage(imageFile, originalImageId = null) {
		const formData = new FormData();

		// Add the image file
		formData.append('image', imageFile);

		// Add the original image ID if provided (for tracking edits)
		if (originalImageId) {
			formData.append('originalImageId', originalImageId);
		}

		// Add metadata
		formData.append('type', 'edited');

		const response = await fetch(`${this.baseUrl}/files/upload`, {
			method: 'POST',
			headers: this.getAuthHeaders(),
			body: formData
		});

		return this.handleResponse(response);
	}

	isAuthenticated() {
		return localStorage.getItem('auth_token') !== null;
	}

	// Create and export a singleton instance
	static instance = new ApiService();
}

export default ApiService.instance;
